import axios, { type AxiosRequestConfig } from 'axios'

import { env } from '@/config/env'

import { toApiError } from './api-error'
import { getEntraAccessToken, notifyUnauthorized } from './auth-token'

export const http = axios.create({
  baseURL: env.apiUrl,
  timeout: env.apiTimeoutMs,
  headers: { 'Content-Type': 'application/json' },
})

http.interceptors.request.use(async (config) => {
  const token = await getEntraAccessToken()
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }
  return config
})

http.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const apiError = toApiError(error)
    if (apiError.isUnauthorized) {
      notifyUnauthorized()
    }
    return Promise.reject(apiError)
  },
)

/** Thin helpers so feature APIs deal in payloads, not axios responses. */
export const api = {
  get: <TResponse>(url: string, config?: AxiosRequestConfig) =>
    http.get<TResponse>(url, config).then((r) => r.data),
  post: <TResponse, TBody = unknown>(url: string, body?: TBody, config?: AxiosRequestConfig) =>
    http.post<TResponse>(url, body, config).then((r) => r.data),
  put: <TResponse, TBody = unknown>(url: string, body?: TBody, config?: AxiosRequestConfig) =>
    http.put<TResponse>(url, body, config).then((r) => r.data),
  patch: <TResponse, TBody = unknown>(url: string, body?: TBody, config?: AxiosRequestConfig) =>
    http.patch<TResponse>(url, body, config).then((r) => r.data),
  delete: <TResponse>(url: string, config?: AxiosRequestConfig) =>
    http.delete<TResponse>(url, config).then((r) => r.data),
}
